import { CampaignDetail } from '@/apis/core/types';
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { convertTimeWithTimezone } from '@/utils/time';
import { getCategoryName } from '../../utils';
import { SelectItem } from '@/types';

interface Props {
  campaign: CampaignDetail;
  rejectionCategories: SelectItem[];
}

const TRUNCATE_LENGTH = 400;

const DeclinedExplanation: React.FC<Props> = ({
  campaign,
  rejectionCategories,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(
    !!(campaign.explanation && campaign.explanation.length > 400)
  );
  const [explanation, setExplanation] = useState(
    isOverflowing
      ? `${`${campaign.explanation?.substring(0, TRUNCATE_LENGTH)}...`}`
      : campaign.explanation
  );

  useEffect(() => {
    if (showExplanation) {
      setExplanation(campaign.explanation);
    } else {
      setExplanation(
        isOverflowing
          ? `${`${campaign.explanation?.substring(0, TRUNCATE_LENGTH)}...`}`
          : campaign.explanation
      );
    }
  }, [showExplanation]);

  useLayoutEffect(() => {
    if (textRef.current) {
      if (textRef.current.scrollHeight > textRef.current.clientHeight) {
        setIsOverflowing(true);
      }
    }
  }, [campaign.explanation]);

  const baseSx = {
    width: '100%',
    wordBreak: 'break-all',
    fontWeight: 400,
  } as const;

  const collapsedSx = {
    ...baseSx,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical',
  };

  return (
    <Flex
      sx={{
        color: 't.black80',
        background: 't.black05',
        borderRadius: 10,
        padding: '16px 20px 20px',
        gap: 'xs',
        mb: 'xl',
      }}
      data-testid="declinedExplanationBlock"
    >
      <Box
        sx={{
          fontSize: 'H300',
          lineHeight: 'normal',
          fontWeight: 600,
          width: 160,
          flexShrink: 0,
        }}
        data-testid="declineDate"
      >
        Declined On:
        <Box as="div" sx={{ fontWeight: 400 }}>
          {convertTimeWithTimezone(campaign.declineDate, 'MM-DD-YYYY')}
        </Box>
      </Box>
      <Box
        sx={{ fontSize: 'H300', fontWeight: 600, lineHeight: 'normal' }}
        data-testid="declinedRejectionCategory"
      >
        {!!campaign.rejectionCategoryList?.length && (
          <Box as="p" sx={{ mb: 'xs' }}>
            Rejection Category:{' '}
            <Box as="span" sx={{ fontWeight: 400 }}>
              {getCategoryName(
                campaign.rejectionCategoryList || [],
                rejectionCategories
              )}
            </Box>
          </Box>
        )}
        <Box
          as="p"
          ref={textRef}
          sx={!showExplanation ? collapsedSx : baseSx}
          data-testid="declinedExplanation"
        >
          <Box as="span" sx={{ fontWeight: 600 }}>
            Declined Explanation:{' '}
          </Box>
          {explanation}
        </Box>
        {isOverflowing && (
          <Box
            as="button"
            sx={{
              background: 'none',
              color: 't.black80',
              fontSize: 'H300',
              fontWeight: 400,
              textDecoration: 'underline',
              cursor: 'pointer',
              mt: 'xs',
              border: 'none',
              padding: 0,
            }}
            onClick={() => setShowExplanation(!showExplanation)}
            data-testid="showMoreButton"
          >
            Show {showExplanation ? 'Less' : 'More'}
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default DeclinedExplanation;
